// @flow
import {
  put,
  select,
  call,
  take,
  fork,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { type ActionType } from 'redux-actions';
import {
  addItem,
  addItems,
  updateObservations,
  updateSiteStatus,
  receiveSiteStatusUpdate,
  commitSiteStatusUpdate,
  requestObservationsUpdate,
  receiveObservationsUpdate,
  commitObservationsUpdate,
  requestAddResponse,
  receiveAddResponse,
  requestDeleteResponse,
  receiveDeleteResponse,
  requestEditResponse,
  receiveEditResponse,
} from './coreDataActions';
// import Ugc from 'models/Ugc';
import { UGC as Ugc, ModerationState, Observation } from 'models';
import { boundedDelay, selectToken, selectObservationMap } from './SagaUtils';
import { Seq } from 'immutable';
import AsyncValues from 'lib/async-value';

import {
  postObservations as postObservationsApi,
  postResponse,
  updateResponse,
  deleteUgc,
  publicationDecision,
} from './coreDataApi';

export default function*(): Generator<*, *, *> {
  yield fork(responseSaga);
  yield takeEvery(requestObservationsUpdate, postObservationsSaga);
  yield takeEvery(updateObservations, publicationDecisionSaga);
  yield takeEvery(updateSiteStatus, siteStatusSaga);
}

export function* publicationDecisionSaga(
  action: ActionType<typeof updateObservations>
): Generator<*, *, *> {
  if (action.meta == null) {
    return;
  }

  const {
    payload: moderationState,
    meta: { clientId, ugctype, ugcClientId },
  } = action;
  const ugcId = moderationState.getUGCId();
  yield put(
    updateObservations(
      ModerationState.setCurrentPublished(moderationState, AsyncValues.Pending)
    )
  );

  const token = yield select(selectToken);
  const observations = getObservationsForPublication(
    moderationState,
    clientId,
    ugcId
  );

  try {
    const response = yield call(publicationDecision, token, {
      clientId,
      ugcClientId,
      ugctype,
      observations,
    });
    yield put(
      updateObservations(
        ModerationState.setCurrentPublished(moderationState, response)
      )
    );
  } catch (err) {
    yield put(
      updateObservations(
        ModerationState.setCurrentPublished(moderationState, err)
      )
    );
  }
}

// when a user changes the site status we first check if simply removing an override
// will satisfy the desired publication status
// if it does we post the observations
// if it does NOT we add the override ant then post the observations
function* siteStatusSaga(
  action: ActionType<typeof updateSiteStatus>
): Generator<*, *, *> {
  const {
    payload: { moderationState, desiredPublished },
    meta,
    meta: { clientId },
  } = action;
  const ugcId = moderationState.getUGCId();
  // there should only be one publication override
  const moderationStateWithoutOverrides = ModerationState.clearPublicationOverrides(
    moderationState,
    clientId
  );
  const observationToAdd = desiredPublished
    ? Observation.PublicationOverrideCode
    : Observation.NotForPublicationOverrideCode;
  const hasNoOverrides = moderationStateWithoutOverrides === moderationState;

  // there were no overrides so we need to check if simply removing the override
  // satifies the desired publication status
  // if it does post the update
  // if it does NOT add the override and post the update
  try {
    // if there are no overrides, add the override, post, exit
    if (hasNoOverrides) {
      const nextModerationState = moderationStateWithoutOverrides.withMutations(
        s => {
          ModerationState.setObservation(s, clientId, observationToAdd);
          ModerationState.applyClientModeration(s, clientId);
          ModerationState.setCurrentPublished(s, desiredPublished);
        }
      );
      yield put(requestObservationsUpdate(nextModerationState, clientId));
      const { error, payload } = yield take(receiveObservationsUpdate);

      // if error alert and revert
      if (!error) {
        yield put(commitSiteStatusUpdate(desiredPublished, ugcId));
      } else {
        throw payload;
      }
    } else {
      // overrides exist so we much check if a new override is necessary
      const token = yield select(selectToken);
      const currentPublished = yield call(publicationDecision, token, {
        ...meta,
        observations: getObservationsForPublication(
          moderationStateWithoutOverrides,
          clientId,
          ugcId
        ),
      });

      const nextModerationState = moderationStateWithoutOverrides.withMutations(
        s => {
          if (currentPublished !== desiredPublished) {
            ModerationState.setObservation(s, clientId, observationToAdd);
          }
          ModerationState.applyClientModeration(s, clientId);
          ModerationState.setCurrentPublished(s, desiredPublished);
        }
      );

      yield put(requestObservationsUpdate(nextModerationState, clientId));
      const { error, payload } = yield take(receiveObservationsUpdate);
      // if errors occurs revert to previous value
      if (!error) {
        yield put(commitSiteStatusUpdate(desiredPublished, ugcId));
      } else {
        throw payload;
      }
    }
  } catch (error) {
    yield put(receiveSiteStatusUpdate(error, ugcId));
    yield call(delay, 2000);
    yield put(receiveSiteStatusUpdate(null, ugcId));
  }
}

export function* addItemTask(payload: {
  data: GetContentResponse,
}): Generator<*, *, *> {
  const data = Array.isArray(payload.data) ? payload.data[0] : payload.data;

  if (!data || !data.ugc) {
    return -1;
  }

  const observationsMap = yield select(selectObservationMap);

  yield put.resolve(addItem({ ...payload, observationsMap }));
  return Ugc.getIdFromData(data);
}

export function* addItemsTask(payload: {
  data: GetContentCollectionResponse,
}): Generator<*, *, *> {
  const { data } = payload;

  if (data.items.length === 0) {
    return data;
  }

  const observationsMap = yield select(selectObservationMap);
  yield put.resolve(addItems({ ...payload, observationsMap }));
  return {
    ...data,
    items: data.items.map(item => Ugc.getIdFromData(item)),
  };
}

export function* postObservationsSaga(
  action: ActionType<typeof requestObservationsUpdate>,
  responseDelay?: ?number,
  commitDelay?: ?number
): Generator<*, *, *> {
  const { payload: moderationState, meta: clientId } = action;
  const ugcId = moderationState.getUGCId();
  const token = yield select(selectToken);

  if (!clientId) {
    yield put(
      receiveObservationsUpdate(
        // $FlowIssue
        new Error('A valid Client Id is required to submit observations'),
        ugcId
      )
    );
    return;
  }

  const observationData = ModerationState.createRequestPayload(
    moderationState,
    clientId
  );
  const ts = Date.now();
  const delayForResponse =
    typeof responseDelay === 'number' ? responseDelay : boundedDelay(ts);
  const delayForCommit = typeof commitDelay === 'number' ? commitDelay : 1000;

  try {
    const response = yield call(postObservationsApi, token, {
      ugcId: ugcId,
      body: observationData,
      clientId,
    });

    yield call(delay, delayForResponse);
    yield put(receiveObservationsUpdate(ugcId));
    yield put.resolve(
      commitObservationsUpdate(
        ModerationState.commitObservationBlobs(
          moderationState,
          clientId,
          response
        ),
        clientId
      )
    );
    yield call(delay, delayForCommit);
  } catch (err) {
    console.log(err);
    yield call(delay, delayForResponse);
    yield put(receiveObservationsUpdate(err, ugcId));
  }
}

export function* respondTask(
  action: ActionType<typeof requestAddResponse>
): Generator<*, *, *> {
  const { payload } = action;
  const token = yield select(selectToken);
  const startTs = Date.now();
  let actionToPut;

  try {
    const response = yield call(postResponse, token, payload);
    actionToPut = receiveAddResponse(
      Ugc.createWithData({ ugc: response }),
      payload.parent_ugc_id
    );
  } catch (error) {
    console.error(error);
    actionToPut = receiveAddResponse(error, payload.parent_ugc_id);
  } finally {
    yield call(delay, boundedDelay(startTs));
    yield put(actionToPut);
  }
}

export function* deleteResponseTask(
  action: ActionType<typeof requestDeleteResponse>
): Generator<*, *, *> {
  const { payload, meta } = action;
  const token = yield select(selectToken);
  const startTs = Date.now();
  let actionToPut;

  try {
    yield call(deleteUgc, token, payload);
    actionToPut = receiveDeleteResponse(payload, meta);
  } catch (error) {
    console.error(error);
    actionToPut = receiveDeleteResponse(error, meta);
  } finally {
    yield call(delay, boundedDelay(startTs));
    yield put(actionToPut);
  }
}

export function* editResponseTask(
  action: ActionType<typeof requestEditResponse>
): Generator<*, *, *> {
  const { payload, meta: parent_ugc_id } = action;
  const token = yield select(selectToken);
  const startTs = Date.now();
  let actionArgs = [];

  try {
    const response = yield call(updateResponse, token, payload);
    actionArgs = [Ugc.createWithData({ ugc: response }), parent_ugc_id];
  } catch (err) {
    console.error(err);
    actionArgs = [err, parent_ugc_id];
  } finally {
    yield call(delay, boundedDelay(startTs));
    yield put(receiveEditResponse(...actionArgs));
  }
}

export function* responseSaga(): Generator<*, *, *> {
  yield takeLatest(requestAddResponse, respondTask);
  yield takeEvery(requestDeleteResponse, deleteResponseTask);
  yield takeLatest(requestEditResponse, editResponseTask);
}

function getObservationsForPublication(
  moderationState: ModerationState,
  clientId: Id,
  ugcId: Id
) {
  return Seq([
    ...ModerationState.getCurrentObservations(moderationState, 1).values(),
    ...ModerationState.getCurrentObservations(
      moderationState,
      clientId
    ).values(),
  ])
    .filter(Observation.isSelected)
    .map(({ selected, ...rest }) => {
      return {
        ...rest,
        client_id: rest.client_id || clientId,
        ugc_id: ugcId,
      };
    })
    .toArray();
}



// WEBPACK FOOTER //
// ./src/ducks/coreDataSaga.js